import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { useBooking } from "@/context/BookingContext";
import { useContent } from "@/context/ContentContext";

const NAV_ITEMS = [
  { id: "home", to: "/", fallback: "Home", key: "nav_label_home" },
  { id: "doctors", to: "/doctors", fallback: "Doctors", key: "nav_label_doctors" },
  { id: "treatments", to: "/treatments", fallback: "Treatments", key: "nav_label_treatments", dropdown: true },
  { id: "results", to: "/gallery", fallback: "Results", key: "nav_label_results" },
  { id: "journal", to: "/blog", fallback: "Journal", key: "nav_label_journal" },
  { id: "contact", to: "/contact", fallback: "Contact", key: "nav_label_contact" },
];

// Royal Signature header — exact reference bar: stacked wordmark left,
// small-caps menu centre (Treatments dropdown), compact black CTA right.
export const RoyalHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const { book } = useBooking();
  const { content } = useContent();
  const location = useLocation();
  const s = content?.settings || {};
  const treatments = (content?.treatments || []).slice(0, 6);
  const announcement = s.announcement;
  // page names are editable in Backoffice → Settings → Navigation
  const LINKS = NAV_ITEMS.map((n) => ({ ...n, label: (s[n.key] || "").trim() || n.fallback }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropOpen(false);
  }, [location.pathname]);

  return (
    <>
      {announcement && (
        <div className="fixed inset-x-0 top-0 z-50 bg-[#0F0F0F] text-center">
          <p data-testid="announcement-banner" className="truncate whitespace-nowrap px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-[#F3E5AB] sm:text-[11px] sm:tracking-[0.18em]">
            {announcement}
          </p>
        </div>
      )}
      <header
        data-testid="royal-header"
        className={`sticky inset-x-0 z-40 border-b transition-all duration-500 ${
          scrolled ? "border-[#E8E2D9] bg-[#FAF8F5]/90 shadow-[0_8px_30px_rgba(15,15,15,0.06)] backdrop-blur-md" : "border-transparent bg-[#FAF8F5]"
        }`}
        style={announcement ? { top: "2.25rem", marginTop: "2.25rem" } : { top: 0 }}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-4 sm:px-8 lg:px-12">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link" aria-label="Royal Skin Clinic home">
            {s.logo_url && <img src={s.logo_url} alt="Royal Skin Clinic logo" className="h-10 w-auto object-contain" />}
            <span className="leading-none">
              <span className="block font-serif text-xl font-medium uppercase tracking-[0.28em] text-[#0F0F0F]">Royal</span>
              <span className="mt-1 block text-[8px] font-semibold uppercase tracking-[0.42em] text-[#6B645C]">Skin Clinic</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {LINKS.map((l) =>
              l.dropdown ? (
                <div key={l.to} className="relative" onMouseEnter={() => setDropOpen(true)} onMouseLeave={() => setDropOpen(false)}>
                  <NavLink
                    to={l.to}
                    data-testid={`nav-link-${l.id}`}
                    className={({ isActive }) =>
                      `flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                        isActive ? "text-[#C5A059]" : "text-[#1A1A1A] hover:text-[#C5A059]"
                      }`
                    }
                  >
                    {l.label}
                    <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${dropOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                  </NavLink>
                  <AnimatePresence>
                    {dropOpen && treatments.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute left-1/2 top-full z-50 mt-3 w-60 -translate-x-1/2 rounded-[5px] border border-[#E8E2D9] bg-white p-2 shadow-[0_20px_50px_rgba(15,15,15,0.12)]"
                        data-testid="nav-treatments-dropdown"
                      >
                        {treatments.map((t) => (
                          <Link
                            key={t.id}
                            to="/treatments"
                            data-testid={`nav-dropdown-treatment-${t.id}`}
                            className="block rounded px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.15em] text-[#4A4A4A] transition-colors hover:bg-[#F8F3E6] hover:text-[#0F0F0F]"
                          >
                            {t.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.id}`}
                  className={({ isActive }) =>
                    `text-[11px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                      isActive ? "text-[#C5A059]" : "text-[#1A1A1A] hover:text-[#C5A059]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <button onClick={() => book()} data-testid="nav-book-consultation-btn" className="btn-royal hidden px-5 py-2.5 text-[10px] sm:inline-flex">
              Book Consultation
            </button>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="nav-mobile-menu-button"
              aria-label="Toggle navigation menu"
              className="flex h-10 w-10 items-center justify-center rounded-[5px] border border-[#E8E2D9] text-[#0F0F0F] transition-colors hover:border-[#C5A059] lg:hidden"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-4 top-20 z-40 rounded-[5px] border border-[#E8E2D9] bg-white/95 p-6 shadow-2xl backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className={({ isActive }) =>
                    `rounded px-4 py-3 text-[11px] font-medium uppercase tracking-[0.18em] transition-colors ${
                      isActive ? "bg-[#F8F3E6] text-[#C5A059]" : "text-[#0F0F0F] hover:bg-[#F5F0EB]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                onClick={() => {
                  setMobileOpen(false);
                  book();
                }}
                data-testid="mobile-book-appointment-button"
                className="btn-royal mt-3 px-5 py-3.5 text-[10px]"
              >
                Book Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
